import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import EmojiSelector from '@/components/ui/emojis/emoji-selector.tsx';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import { type Comment } from '@/models/registry';
import { EmojiReactionType } from '@/models/types.ts';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useRef, useState } from 'react';
import CommentMenu from './comment-menu';
import CommentReplyHeader from './comment-reply-header';
import Reply from './reply';
import AddReplyTipTap from './tip-tap-editor/add-reply-tiptap';
import EditCommentTiptap from './tip-tap-editor/edit-comment-tiptap';
import MessageTipTap from './tip-tap-editor/message-tiptap';

const calculateWasCreatedRecently = (createdAt: string) => {
  return dayjs(createdAt).add(1, 'second').isAfter(dayjs());
};

const Thread = observer(
  ({ comment, dealId }: { comment: Comment; dealId: string }) => {
    const { content } = comment;
    const replies = comment.replies;
    const { id: userId } = useUser();
    const threadIncludesReplyFromMe = replies.some(
      (reply) => reply.created_by && reply.created_by.id === userId,
    );

    const [showAddReply, setShowAddReply] = useState(threadIncludesReplyFromMe);

    const wasCreatedRecently = calculateWasCreatedRecently(comment.created_at);

    const shouldAnimate = wasCreatedRecently;

    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'comment',
      id: comment.id,
    });

    const commentHoverRef = useRef<HTMLDivElement>(null);

    const [isEditingComment, setIsEditingComment] = useState(false);

    return (
      <motion.div
        initial={{
          height: shouldAnimate ? 0 : 'auto',
          opacity: shouldAnimate ? 0 : 1,
        }}
        animate={{ height: 'auto', opacity: 1 }}
        onClick={() => setShowAddReply(true)}
        ref={focusRef}
      >
        <Card className={cn('p-4 flex flex-col gap-0 px-0 pb-0')}>
          <CardHeader
            className={cn('p-0 px-4', {
              'border-1 border-primary': shouldShowFocus,
            })}
            ref={commentHoverRef}
          >
            <CardTitle className="flex flex-row justify-between px-0">
              <CommentReplyHeader
                commentOrReply={comment}
                isEditingComment={isEditingComment}
              />

              <div className="flex flex-row">
                <EmojiSelector
                  targetId={comment.id}
                  hoverRef={commentHoverRef}
                  targetType={EmojiReactionType.Comment}
                />
                <CommentMenu
                  comment={comment}
                  dealId={dealId}
                  hoverRef={commentHoverRef}
                  setIsEditingComment={setIsEditingComment}
                />
              </div>
            </CardTitle>
            {isEditingComment ? (
              <EditCommentTiptap
                dealId={dealId}
                comment={comment}
                setIsEditingComment={setIsEditingComment}
              />
            ) : (
              <CardDescription className="pb-2 whitespace-pre-wrap text-uneditable">
                <MessageTipTap content={content} />
              </CardDescription>
            )}
            {'emoji_reactions' in comment &&
              !isEmpty(comment.emoji_reactions) && (
                <EmojiReactions
                  targetId={comment.id}
                  targetType={EmojiReactionType.Comment}
                  className="min-h-6 pb-2"
                  reactionButtonClassName="h-6 text-xs leading-none px-2"
                  selectorButtonClassName="h-6 w-6 p-0 rounded-md leading-none"
                />
              )}
          </CardHeader>

          {!isEmpty(replies) && (
            <>
              <div className="border-t w-full"></div>
              {replies.map((reply) => {
                const shouldAnimate = calculateWasCreatedRecently(
                  reply.created_at,
                );

                return (
                  <motion.div
                    key={reply.id}
                    initial={{
                      height: shouldAnimate ? 0 : 'auto',
                      opacity: shouldAnimate ? 0 : 1,
                    }}
                    animate={{ height: 'auto', opacity: 1 }}
                  >
                    <Reply reply={reply} dealId={dealId} />
                  </motion.div>
                );
              })}
            </>
          )}

          {showAddReply && (
            <motion.div
              className="border-t px-4"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <AddReplyTipTap commentId={comment.id} dealId={dealId} />
            </motion.div>
          )}
        </Card>
      </motion.div>
    );
  },
);

export default Thread;
